import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText, FolderCheck, ArrowRight, ShieldCheck, Sparkles, CheckCircle2, SearchCheck, Users, Wrench, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { RecheckBanner } from '../components/RecheckBanner';
import { StreakRing } from '../components/StreakRing';
import { ImpactStats } from '../components/ImpactStats';
import { SuccessStories } from '../components/SuccessStories';
import { useLang } from '../context/LangContext';

const CTAS = [
  { title: 'Find Benefits', desc: '5-minute plain-language eligibility check', icon: Search, to: '/find-benefits', color: 'bg-primary text-primary-foreground', testId: 'cta-find-benefits' },
  { title: 'My Applications', desc: 'Track ODSP, DTC, CPP-D, ADP and housing', icon: FileText, to: '/applications', color: 'bg-[#C26227] text-white', testId: 'cta-my-applications' },
  { title: 'My Documents', desc: 'Store medical notes, ID, tax forms & vault', icon: FolderCheck, to: '/documents', color: 'bg-[#1E5888] text-white', testId: 'cta-my-documents' },
  { title: 'Equipment Funding', desc: 'Stack ADP + ODSP + charities for wheelchairs, hearing aids & more', icon: Wrench, to: '/equipment', color: 'bg-[#5B5F97] text-white', testId: 'cta-equipment' },
];

const BULLETS = [
  'Personalized eligibility screening (Ontario + Federal)',
  'Plain-language "Why did I match?" transparency',
  'Personalized document checklists',
  'Application tracker with reminders',
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <div className="relative">
      <RecheckBanner />
      <StreakRing />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-10 md:pt-20 md:pb-16">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6" data-testid="hero-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> {t('Free · WCAG-first · Never asks for CRA passwords')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" style={{fontFamily: 'Lexend'}}>
              {t('Ontario benefits, made')} <span className="text-primary">{t('clear')}</span>. {t('Made')} <span className="text-[#C26227]">{t('actionable')}</span>.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t('Answer a few plain-language questions and get the exact provincial and federal disability programs you may qualify for — with document checklists, step-by-step instructions, and a personal application tracker.')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate(user ? '/find-benefits' : '/login')} data-testid="hero-find-benefits">
                {t('Find My Benefits')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-6" onClick={() => navigate('/directory')} data-testid="hero-browse-directory">
                {t('Browse the Benefits Directory')}
              </Button>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-foreground/80">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{t(b)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-primary/5 rounded-3xl -z-10 grain" />
            <img src="https://images.unsplash.com/photo-1617803676663-3aa36f6d148c?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Peaceful Ontario landscape" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5]" />
            <div className="absolute -bottom-5 -left-5 bg-card border border-border rounded-xl p-4 shadow-lg max-w-[240px]" data-testid="hero-callout">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Sparkles className="w-3.5 h-3.5 text-primary" /> Live example</div>
              <div className="text-sm font-medium leading-snug">"Based on your answers, we found <span className="text-primary font-bold">8 programs</span> you may qualify for."</div>
            </div>
          </div>
        </div>
      </section>

      {/* Three CTA cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {CTAS.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                data-testid={c.testId}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-8 hover:border-primary/50 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className={`w-12 h-12 rounded-xl ${c.color} flex items-center justify-center mb-5 group-hover:scale-105 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold" style={{fontFamily: 'Lexend'}}>{t(c.title)}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{t(c.desc)}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                  {t('Continue')} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ODSP audit + Partner mode */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          <button onClick={() => navigate(user ? '/missing-benefits' : '/login')} data-testid="cta-missing-benefits" className="text-left group rounded-2xl border-2 border-[#C26227]/30 bg-[#C26227]/5 p-6 md:p-8 hover:border-[#C26227] transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <div className="flex items-center gap-3 mb-3"><div className="w-11 h-11 rounded-xl bg-[#C26227] text-white flex items-center justify-center"><SearchCheck className="w-5 h-5" /></div><span className="text-xs font-semibold uppercase tracking-wide text-[#C26227]">{t('One-tap audit')}</span></div>
            <h3 className="text-xl font-semibold" style={{fontFamily: 'Lexend'}}>{t('I already receive ODSP — what am I missing?')}</h3>
            <p className="text-sm text-muted-foreground mt-1.5">{t('Most ODSP recipients never claim the DTC, Canada Disability Benefit, RDSP bonds or ADP equipment funding. Find your gaps in 10 seconds.')}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#C26227]">{t('Run my audit')} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></div>
          </button>
          <button onClick={() => navigate(user ? '/partner' : '/login')} data-testid="cta-partner-mode" className="text-left group rounded-2xl border-2 border-[#1E5888]/30 bg-[#1E5888]/5 p-6 md:p-8 hover:border-[#1E5888] transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <div className="flex items-center gap-3 mb-3"><div className="w-11 h-11 rounded-xl bg-[#1E5888] text-white flex items-center justify-center"><Users className="w-5 h-5" /></div><span className="text-xs font-semibold uppercase tracking-wide text-[#1E5888]">{t('For social workers & agencies')}</span></div>
            <h3 className="text-xl font-semibold" style={{fontFamily: 'Lexend'}}>{t('Community Partner Mode')}</h3>
            <p className="text-sm text-muted-foreground mt-1.5">{t('Pre-fill a benefits checklist for your client and hand it off with one shareable link. It lands straight in their tracker.')}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E5888]">{t('Build a checklist')} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></div>
          </button>
        </div>
        <button onClick={() => navigate(user ? '/appeals' : '/login')} data-testid="cta-appeals" className="mt-4 w-full text-left group rounded-2xl border-2 border-border bg-card p-5 hover:border-primary transition-all flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-secondary text-primary flex items-center justify-center shrink-0"><Scale className="w-5 h-5" /></div>
          <div className="flex-1"><div className="font-semibold" style={{fontFamily: 'Lexend'}}>{t('Denied? Appeal it')}</div><div className="text-sm text-muted-foreground">{t('Plain-language steps and deadline calculator for ODSP, DTC and CPP-D denials')}</div></div>
          <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform" />
        </button>
      </section>

      <ImpactStats />
      <SuccessStories />

      {/* Always free */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <div className="rounded-2xl bg-primary text-primary-foreground p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4" data-testid="free-banner">
          <div className="text-xs font-bold uppercase tracking-wider bg-white/15 rounded-full px-3 py-1 w-fit">{t('Always free')}</div>
          <p className="text-base md:text-lg font-medium">{t('100% free, forever. No premium tier, no hidden fees — every feature you see is free for everyone.')}</p>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-secondary/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>{t('How it works')}</h2>
          <p className="text-muted-foreground mt-2 max-w-2xl">{t('You focus on your life. We handle the government paperwork navigation.')}</p>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {[
              { n: '01', t: 'Tell us about you', d: 'A few plain-language questions — with an "I\'m not sure" option on every one.' },
              { n: '02', t: 'See your matches', d: 'Personalized list of Ontario + Federal + Toronto programs with match strength.' },
              { n: '03', t: 'Prepare with confidence', d: 'Document checklists tailored to each program. Upload once, use everywhere.' },
              { n: '04', t: 'Track & follow up', d: 'Log reference numbers, deadlines, next actions. Government forms open in a new tab.' },
            ].map((s) => (
              <div key={s.n} className="bg-card border border-border rounded-xl p-5">
                <div className="text-primary font-bold text-sm">{s.n}</div>
                <div className="font-semibold mt-1.5" style={{fontFamily: 'Lexend'}}>{t(s.t)}</div>
                <p className="text-sm text-muted-foreground mt-1.5">{t(s.d)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
